import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { router } from "@inertiajs/react"
import { Play, Terminal, Copy } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/cn"
import { HttpMethodBadge } from "./http-method-badge"
import type { Endpoint, HttpMethod, QuerySchemaParam, TestInvokeResult } from "@/types/endpoint"

type Props = {
  endpoint: Endpoint | null
  defaultSourceKey: string | null
  csrfToken: string
}

function statusTone(status: number | null): "success" | "warning" | "error" | "neutral" {
  if (status == null) return "error"
  if (status >= 200 && status < 300) return "success"
  if (status >= 300 && status < 400) return "neutral"
  if (status >= 400 && status < 500) return "warning"
  return "error"
}

type QuerySchemaParamLike = QuerySchemaParam | string | null | undefined

function normalizeQuerySchema(raw: QuerySchemaParamLike[] | undefined): QuerySchemaParam[] {
  return (raw ?? [])
    .map((param) => {
      if (typeof param === "string") return { name: param }
      return param
    })
    .filter((param): param is QuerySchemaParam => Boolean(param?.name))
}

async function readJson<T>(resp: Response): Promise<T> {
  const text = await resp.text()
  if (!text.trim()) return {} as T

  try {
    return JSON.parse(text) as T
  } catch {
    const contentType = resp.headers.get("content-type") ?? ""
    const bodyKind = contentType.includes("html") || text.trimStart().startsWith("<") ? "HTML" : "non-JSON"
    throw new Error(`Expected JSON from Valet but received ${bodyKind} (${resp.status})`)
  }
}

function queryParamDetails(param: QuerySchemaParam): string | null {
  const bits = [
    param.type,
    param.format ? `format=${param.format}` : null,
    param.default != null ? `default=${String(param.default)}` : null,
  ].filter(Boolean)
  return bits.length > 0 ? bits.join(" · ") : null
}

function coercePreviewValue(value: string, type: string | undefined): unknown {
  if (type === "integer" && /^-?\d+$/.test(value)) return Number(value)
  if (type === "number" && /^-?\d+$/.test(value)) return Number(value)
  if (type === "number" && /^-?\d+\.\d+$/.test(value)) return Number(value)
  if (type === "boolean" && value === "true") return true
  if (type === "boolean" && value === "false") return false
  return value
}

export function EndpointTestPanel({ endpoint, defaultSourceKey, csrfToken }: Props) {
  const [sourceKey, setSourceKey] = useState(endpoint?.testSourceKey ?? defaultSourceKey ?? "")
  const [sourceKeyTouched, setSourceKeyTouched] = useState(false)
  const [lastSavedSourceKey, setLastSavedSourceKey] = useState(endpoint?.testSourceKey ?? "")
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle")
  const savedSourceKeysRef = useRef<Record<number, string>>({})
  const [queryValues, setQueryValues] = useState<Record<string, string>>({})
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<TestInvokeResult | null>(null)
  const [tab, setTab] = useState<"body" | "headers">("body")
  const [curlLoading, setCurlLoading] = useState(false)
  const [curlStatus, setCurlStatus] = useState<{ kind: "ok" | "err"; msg: string } | null>(null)
  const querySchema = useMemo(
    () => normalizeQuerySchema(endpoint?.querySchema as QuerySchemaParamLike[] | undefined),
    [endpoint?.querySchema],
  )
  const endpointId = endpoint?.id ?? null
  const defaultParamLocation = endpoint?.httpMethod === "GET" ? "query" : "body"
  const requestParamLabel = defaultParamLocation === "query" ? "Query params" : "Request body params"

  // Reset the source_key field whenever the operator selects a different
  // endpoint — each endpoint remembers its own last-tested key.
  useEffect(() => {
    if (!endpoint) return
    const savedSourceKey = savedSourceKeysRef.current[endpoint.id] ?? endpoint.testSourceKey ?? ""
    setSourceKey(savedSourceKey || defaultSourceKey || "")
    setSourceKeyTouched(false)
    setLastSavedSourceKey(savedSourceKey)
    setSaveStatus("idle")
    setResult(null)
    setQueryValues({})
  }, [endpoint?.id, endpoint?.testSourceKey, defaultSourceKey])

  useEffect(() => {
    if (saveStatus !== "saved") return
    const t = window.setTimeout(() => setSaveStatus("idle"), 1500)
    return () => window.clearTimeout(t)
  }, [saveStatus])

  const persistSourceKey = useCallback(async (next: string) => {
    if (endpointId == null) return

    const trimmed = next.trim()
    if (trimmed === lastSavedSourceKey) return

    const body = new URLSearchParams()
    body.set("source_key", trimmed)
    setSaveStatus("saving")

    try {
      const resp = await fetch(`/endpoints/${endpointId}/test_source_key`, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json",
        },
        credentials: "same-origin",
        body,
      })
      if (!resp.ok) throw new Error(`save failed (${resp.status})`)
      savedSourceKeysRef.current[endpointId] = trimmed
      setLastSavedSourceKey(trimmed)
      setSaveStatus("saved")
    } catch {
      setSaveStatus("idle")
    }
  }, [csrfToken, endpointId, lastSavedSourceKey])

  useEffect(() => {
    if (!sourceKeyTouched) return
    if (sourceKey.trim() === lastSavedSourceKey) return
    const t = window.setTimeout(() => { void persistSourceKey(sourceKey) }, 600)
    return () => window.clearTimeout(t)
  }, [lastSavedSourceKey, persistSourceKey, sourceKey, sourceKeyTouched])

  const requestValuesFor = useCallback((location: "query" | "body") => {
    if (!endpoint) return {}
    if (querySchema.length === 0) return location === defaultParamLocation ? queryValues : {}

    const allowed = new Set(
      querySchema
        .filter((p) => (p.location ?? defaultParamLocation) === location)
        .map((p) => p.name),
    )
    return Object.fromEntries(
      Object.entries(queryValues).filter(([key, value]) => allowed.has(key) && value != null && String(value).trim() !== ""),
    )
  }, [defaultParamLocation, endpoint, querySchema, queryValues])

  const requestQueryValues = useMemo(() => requestValuesFor("query"), [requestValuesFor])
  const requestBodyValues = useMemo(() => requestValuesFor("body"), [requestValuesFor])
  const requestBodyPreviewValues = useMemo(() => {
    if (Object.keys(requestBodyValues).length === 0) return {}
    const byName = new Map(querySchema.map((p) => [p.name, p]))
    return Object.fromEntries(
      Object.entries(requestBodyValues).map(([key, value]) => [
        key,
        coercePreviewValue(value, byName.get(key)?.type),
      ]),
    )
  }, [querySchema, requestBodyValues])

  const interpolatedPath = useMemo(() => {
    if (!endpoint) return ""
    const filled = endpoint.pathTemplate.replace("{source_key}", encodeURIComponent(sourceKey || "{source_key}"))
    const qs = Object.entries(requestQueryValues)
      .filter(([, v]) => v != null && String(v).trim() !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&")
    return qs ? `${filled}?${qs}` : filled
  }, [endpoint?.pathTemplate, sourceKey, requestQueryValues])

  const missingRequired = useMemo(() => {
    if (!endpoint) return false
    return querySchema.some(
      (p) => p.required && !(queryValues[p.name] && queryValues[p.name].trim()),
    )
  }, [endpoint, querySchema, queryValues])

  if (!endpoint) {
    return (
      <Card className="flex items-center justify-center p-6 text-sm text-app-muted">
        Select an endpoint to test it.
      </Card>
    )
  }

  const handleRun = async () => {
    setRunning(true)
    void persistSourceKey(sourceKey)
    try {
      const resp = await fetch(`/endpoints/${endpoint.id}/test_invoke`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json",
        },
        body: JSON.stringify({ source_key: sourceKey, query: queryValues }),
      })
      const json = await readJson<TestInvokeResult>(resp)
      setResult(json)
      if (json.status != null && json.status >= 200 && json.status < 300) {
        router.reload({ only: ["endpoints"] })
      }
    } catch (err) {
      setResult({
        status: null, statusLabel: "Network error",
        body: null, error: String(err),
        latencyMs: 0, requestPath: endpoint.pathTemplate, requestUrl: null, requestMethod: endpoint.httpMethod,
        responseHeaders: {}
      })
    } finally {
      setRunning(false)
    }
  }

  const copyBody = () => {
    if (result?.body == null) return
    const text = typeof result.body === "string" ? result.body : JSON.stringify(result.body, null, 2)
    navigator.clipboard?.writeText(text)
  }

  const copyCurl = async () => {
    if (!endpoint) return
    setCurlLoading(true)
    setCurlStatus(null)
    void persistSourceKey(sourceKey)
    try {
      const resp = await fetch(`/endpoints/${endpoint.id}/curl_command`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json",
        },
        body: JSON.stringify({ source_key: sourceKey, query: queryValues }),
      })
      const json = await readJson<{ curl?: string }>(resp)
      if (typeof json.curl !== "string") {
        setCurlStatus({ kind: "err", msg: "Failed to build curl" })
        return
      }
      await navigator.clipboard?.writeText(json.curl)
      setCurlStatus({ kind: "ok", msg: "Copied — paste into a shell. Treat as a secret." })
    } catch (err) {
      setCurlStatus({ kind: "err", msg: String(err) })
    } finally {
      setCurlLoading(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card className="overflow-hidden p-0">
        <header className="border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-app-text">Test endpoint</h2>
          <p className="mt-0.5 text-sm text-app-muted">
            Run the endpoint live to verify shape before wiring it to a skill.
          </p>
        </header>

        <div className="space-y-5 p-5">
          <Section number={1} title="Inputs">
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-wide text-app-muted">Source key</label>
              <Input
                type="text"
                value={sourceKey}
                onChange={(e) => {
                  setSourceKey(e.target.value)
                  setSourceKeyTouched(true)
                }}
                onBlur={() => setSourceKey((v) => v.trim())}
                placeholder="usr_…"
                className="w-full font-mono"
              />
              <p className="mt-1 text-xs text-app-muted">
                Defaults to the latest appuser source_key for this company.
                {saveStatus === "saving" && <span className="ml-2">Saving...</span>}
                {saveStatus === "saved" && <span className="ml-2 text-status-success">Saved</span>}
              </p>
            </div>

            {querySchema.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wide text-app-muted">{requestParamLabel}</div>
                {querySchema.map((p) => (
                  <div key={p.name}>
                    <label className="mb-1 flex flex-wrap items-center gap-2 text-xs text-app-muted">
                      <span className="font-mono text-app-text">{p.name}</span>
                      {(p.location ?? defaultParamLocation) !== defaultParamLocation && (
                        <span className="rounded-full border border-border bg-white/5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-app-muted">
                          {(p.location ?? defaultParamLocation) === "query" ? "Query" : "Body"}
                        </span>
                      )}
                      {p.required && (
                        <span className="rounded-full border border-status-error/40 bg-status-error/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-status-error">
                          Required
                        </span>
                      )}
                      {queryParamDetails(p) && <span>{queryParamDetails(p)}</span>}
                    </label>
                    <Input
                      type="text"
                      value={queryValues[p.name] ?? ""}
                      onChange={(e) => setQueryValues((q) => ({ ...q, [p.name]: e.target.value }))}
                      placeholder={p.required ? "required" : "optional"}
                      className="w-full font-mono"
                    />
                    {p.description && <p className="mt-1 text-xs text-app-muted">{p.description}</p>}
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section number={2} title="Request preview">
            <div className="flex items-center gap-2 rounded-control border border-border bg-app-panel3 px-3 py-2">
              <HttpMethodBadge method={endpoint.httpMethod} />
              <span className="truncate font-mono text-xs text-app-text">{interpolatedPath}</span>
            </div>
            {endpoint.httpMethod !== "GET" && Object.keys(requestBodyPreviewValues).length > 0 && (
              <pre className="overflow-x-auto rounded-control border border-border bg-app-panel3 p-3 font-mono text-xs text-app-text">
                {JSON.stringify(requestBodyPreviewValues, null, 2)}
              </pre>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="red"
                size="sm"
                onClick={handleRun}
                disabled={running || !sourceKey.trim() || missingRequired}
              >
                <Play size={14} className="mr-1.5" />
                {running ? "Sending…" : "Test endpoint"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={copyCurl}
                disabled={curlLoading || !sourceKey.trim() || missingRequired}
                title="Build a terminal curl command (with auth headers) and copy it to your clipboard"
              >
                <Terminal size={14} className="mr-1.5" />
                {curlLoading ? "Building…" : "Copy curl"}
              </Button>
            </div>
            {curlStatus && (
              <div className={cn(
                "text-xs",
                curlStatus.kind === "ok" ? "text-status-success" : "text-status-error",
              )}>
                {curlStatus.msg}
              </div>
            )}
          </Section>
        </div>
      </Card>

      {endpoint.positiveSampleResponse != null && (
        <Card className="overflow-hidden p-0">
          <header className="border-b border-border px-5 py-4">
            <h2 className="text-base font-semibold text-app-text">Saved positive sample</h2>
            <p className="mt-0.5 text-sm text-app-muted">
              Forge uses this successful response shape when drafting tests and workflow predicates.
            </p>
          </header>
          <div className="space-y-3 p-5">
            <div className="flex flex-wrap gap-2 text-xs text-app-muted">
              {endpoint.positiveSampleSourceKey && (
                <Badge tone="neutral" className="font-mono">
                  source_key: {endpoint.positiveSampleSourceKey}
                </Badge>
              )}
              {endpoint.positiveSampleCapturedAt && (
                <Badge tone="neutral">
                  saved {new Date(endpoint.positiveSampleCapturedAt).toLocaleString()}
                </Badge>
              )}
              {Object.keys(endpoint.positiveSampleQuery ?? {}).length > 0 && (
                <Badge tone="neutral" className="font-mono">
                  {defaultParamLocation === "query" ? "query" : "body"}: {JSON.stringify(endpoint.positiveSampleQuery)}
                </Badge>
              )}
            </div>
            <pre className="max-h-[24vh] overflow-auto whitespace-pre-wrap break-words rounded-control border border-border bg-app-panel3 p-3 font-mono text-xs text-app-text">
              {typeof endpoint.positiveSampleResponse === "string"
                ? endpoint.positiveSampleResponse
                : JSON.stringify(endpoint.positiveSampleResponse, null, 2)}
            </pre>
          </div>
        </Card>
      )}

      {result && (
        <Card className="overflow-hidden p-0">
          <header className="flex items-center justify-between border-b border-border px-5 py-4">
            <div>
              <h2 className="text-base font-semibold text-app-text">Response</h2>
              <p className="mt-0.5 text-sm text-app-muted">
                Completed in {result.latencyMs}ms.
              </p>
            </div>
            <div
              className={cn(
                "h-2 w-2 rounded-full",
                result.status && result.status >= 200 && result.status < 300
                  ? "bg-status-success"
                  : "bg-status-error",
              )}
              aria-hidden
            />
          </header>

          <div className="border-b border-border px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="text-3xl font-semibold tabular-nums text-app-text">
                {result.status ?? "—"}
              </div>
              <div className="space-y-0.5">
                <Badge tone={statusTone(result.status)}>{result.statusLabel ?? "—"}</Badge>
                {result.error && <div className="text-xs text-status-error">{result.error}</div>}
              </div>
            </div>
            {result.requestUrl && (
              <div className="mt-3 flex items-center gap-2 rounded-control border border-border bg-app-panel3 px-3 py-2">
                <HttpMethodBadge method={result.requestMethod as HttpMethod} />
                <code className="flex-1 truncate font-mono text-xs text-app-text" title={result.requestUrl}>
                  {result.requestUrl}
                </code>
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(result.requestUrl ?? "")}
                  className="rounded-control border border-border px-2 py-0.5 text-[11px] text-app-muted transition hover:bg-white/5 hover:text-app-text"
                  title="Copy URL"
                >
                  <Copy size={11} />
                </button>
              </div>
            )}
            {result.status != null && result.status >= 200 && result.status < 300 && (
              <div className="mt-3 rounded-control border border-status-success/30 bg-status-success/10 px-3 py-2 text-xs text-status-success">
                Saved as this endpoint's positive sample for Forge.
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 border-b border-border px-5">
            <button
              type="button"
              onClick={() => setTab("body")}
              className={cn(
                "relative -mb-px border-b-2 px-3 py-2.5 text-xs font-medium transition",
                tab === "body"
                  ? "border-brand-red text-brand-red"
                  : "border-transparent text-app-muted hover:text-app-text",
              )}
            >
              Response body
            </button>
            <button
              type="button"
              onClick={() => setTab("headers")}
              className={cn(
                "relative -mb-px border-b-2 px-3 py-2.5 text-xs font-medium transition",
                tab === "headers"
                  ? "border-brand-red text-brand-red"
                  : "border-transparent text-app-muted hover:text-app-text",
              )}
            >
              Headers
            </button>
            <div className="ml-auto py-2">
              {tab === "body" && result.body != null && (
                <button
                  type="button"
                  onClick={copyBody}
                  className="rounded-control border border-border px-2 py-1 text-xs text-app-muted transition hover:bg-white/5 hover:text-app-text"
                >
                  <span className="inline-flex items-center gap-1">
                    <Copy size={11} />
                    Copy
                  </span>
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[40vh] overflow-auto p-5">
            {tab === "body" ? (
              <pre className="whitespace-pre-wrap break-words font-mono text-xs text-app-text">
                {result.body == null ? "(no body)" :
                 typeof result.body === "string" ? result.body :
                 JSON.stringify(result.body, null, 2)}
              </pre>
            ) : (
              Object.keys(result.responseHeaders ?? {}).length === 0 ? (
                <div className="font-mono text-xs text-app-muted">
                  (no response headers — request never reached the partner)
                </div>
              ) : (
                <table className="w-full font-mono text-[11px]">
                  <tbody className="divide-y divide-border">
                    {Object.entries(result.responseHeaders).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => (
                      <tr key={k}>
                        <td className="whitespace-nowrap pr-3 py-1 align-top text-app-muted">{k}</td>
                        <td className="break-all py-1 text-app-text">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        </Card>
      )}

      {endpoint.errorStats.last30dCount > 0 && (
        <Card className="px-5 py-3 text-xs text-app-muted">
          {endpoint.errorStats.last30dCount} errors in last 30 days · {endpoint.errorStats.last24hCount} in last 24h. Click the row sparkline for details.
        </Card>
      )}
    </div>
  )
}

function Section({
  number,
  title,
  children,
}: {
  number: number
  title: string
  children: ReactNode
}) {
  return (
    <section className="space-y-3">
      <header className="flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-app-panel3 text-[11px] font-semibold text-app-muted">
          {number}
        </span>
        <h3 className="text-base font-semibold text-app-text">{title}</h3>
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  )
}
